/**
 * How the report states the reference an angle is read against.
 *
 * Clinicians read these angles as deviations from neutral, so the reference
 * is 0° wherever 0° is normal (nearly every angle: tilts, shifts, rotations),
 * with how far from it is still normal beside it. An angle whose normal range
 * does not take in 0° — a knee or elbow near straight, the craniovertebral
 * angle — keeps its range. Wording only: the grade still comes from the
 * stored range (metricBands.js).
 */

/** Whether 0° is within the normal range. */
export function zeroIsNormal(range) {
  return Array.isArray(range) && range.length === 2 && range[0] <= 0 && range[1] >= 0;
}

const deg = v => `${String(v).replace('-', '−')}°`;

/** The reference: "0°", or the range when 0° is not normal ("160–185°"). */
export function referenceLabel(range) {
  if (!Array.isArray(range) || range.length !== 2) return '—';
  if (zeroIsNormal(range)) return '0°';
  return range[0] < 0 || range[1] < 0 ? `${deg(range[0])} to ${deg(range[1])}` : `${range[0]}–${range[1]}°`;
}

/** How far from 0° is still normal ("normal up to 3°"), or null when the reference is a range. */
export function toleranceLabel(range) {
  if (!zeroIsNormal(range)) return null;
  if (range[0] === 0) return `normal up to ${deg(range[1])}`;
  return `normal ${deg(range[0])} to ${deg(range[1])}`;
}

/** Both together, for a line of text: "0° (normal up to 3°)". */
export function referenceText(range) {
  const tol = toleranceLabel(range);
  return tol ? `${referenceLabel(range)} (${tol})` : referenceLabel(range);
}
